import React, { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  AiOutlineCalendar,
  AiOutlineCheckCircle,
  AiOutlinePercentage,
  AiOutlineSearch,
} from "react-icons/ai";
import Sidebaradmin from "../Sidebar";
import Navbaradmin from "../navbar";
import ExistingDynamicPricingRules from "./ExistingDynamicPricingRules";

const emptyPricingData = {
  productId: "",
  basePrice: "",
  salePrice: "",
  condition: "",
  startDate: "",
  endDate: "",
  freeMonths: 0,
};

const inputClass =
  "h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50 disabled:text-slate-500";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-sm font-semibold text-slate-700">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const formatMoney = (value) => {
  const number = Number(value || 0);
  return `$${number.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const getDiscountPercent = (basePrice, salePrice) => {
  const base = Number(basePrice || 0);
  const sale = Number(salePrice || 0);
  if (!base || sale >= base) return 0;
  return Math.round(((base - sale) / base) * 100);
};

const DynamicPricingAdminPage = () => {
  const [products, setProducts] = useState([]);
  const [dynamicPricingList, setDynamicPricingList] = useState([]);
  const [pricingData, setPricingData] = useState(emptyPricingData);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetch("/api/products")
      .then((response) => response.json())
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.log("Error fetching products:", err);
        setError("Failed to load products");
      });
  }, []);

  useEffect(() => {
    fetch("/api/dynamic-pricing/products")
      .then((response) => response.json())
      .then((data) => {
        if (data && Array.isArray(data.products)) {
          setDynamicPricingList(data.products);
        } else {
          setError("Failed to load dynamic pricing rules");
        }
      })
      .catch((err) => {
        console.log("Error fetching dynamic pricing:", err);
        setError("Failed to load dynamic pricing");
      });
  }, []);

  const clearMessages = () => {
    setError("");
    setSuccessMessage("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    clearMessages();

    if (name === "productId") {
      const selectedProduct = products.find((product) => product._id === value);
      setPricingData((prevData) => ({
        ...prevData,
        productId: value,
        basePrice: selectedProduct?.totalPrice || selectedProduct?.price || "",
      }));
      return;
    }

    setPricingData((prevData) => ({ ...prevData, [name]: value }));
  };

  const validatePricing = () => {
    const { productId, basePrice, salePrice, condition, startDate, endDate } = pricingData;
    if (!productId || !basePrice || !salePrice || !condition || !startDate || !endDate) {
      setError("All pricing fields are required.");
      return false;
    }
    return true;
  };

  const handleAddPricing = () => {
    if (!validatePricing()) return;

    setLoading(true);
    fetch("/api/dynamic-pricing", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(pricingData),
    })
      .then((response) => response.json())
      .then((data) => {
        setSuccessMessage("Pricing rule added successfully");
        if (data?.pricing) {
          setDynamicPricingList((prevList) => [...prevList, data.pricing]);
        }
        setPricingData(emptyPricingData);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to add pricing rule");
      })
      .finally(() => setLoading(false));
  };

  const handleUpdatePricing = () => {
    if (!editingId || !validatePricing()) return;

    setLoading(true);
    fetch(`/api/dynamic-pricing/${editingId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(pricingData),
    })
      .then((response) => response.json())
      .then((data) => {
        const updatedRule = data?.dynamicPricing || data?.pricing;
        setSuccessMessage("Pricing rule updated successfully");
        if (updatedRule) {
          setDynamicPricingList((prevList) =>
            prevList.map((item) => (item._id === editingId ? { ...item, ...updatedRule } : item))
          );
        }
        setEditingId(null);
        setPricingData(emptyPricingData);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to update pricing rule");
      })
      .finally(() => setLoading(false));
  };

  const handleStartEditPricing = (pricing) => {
    const matchedProduct = products.find(
      (product) => product._id === pricing.productId || product.name === pricing.productName
    );

    clearMessages();
    setEditingId(pricing._id);
    setPricingData({
      productId: pricing.productId || pricing.product?._id || matchedProduct?._id || "",
      basePrice: pricing.basePrice || "",
      salePrice: pricing.salePrice || "",
      condition: pricing.condition || "",
      startDate: pricing.startDate ? String(pricing.startDate).slice(0, 10) : "",
      endDate: pricing.endDate ? String(pricing.endDate).slice(0, 10) : "",
      freeMonths: pricing.freeMonths || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeletePricing = (id) => {
    const shouldDelete = window.confirm("Are you sure you want to delete this pricing rule?");
    if (!shouldDelete) return;

    setLoading(true);
    fetch(`/api/dynamic-pricing/${id}`, { method: "DELETE" })
      .then(() => {
        setSuccessMessage("Pricing rule deleted successfully");
        setDynamicPricingList((prevList) => prevList.filter((item) => item._id !== id));
        if (editingId === id) {
          setEditingId(null);
          setPricingData(emptyPricingData);
        }
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to delete pricing rule");
      })
      .finally(() => setLoading(false));
  };

  const filteredPricingList = useMemo(
    () =>
      dynamicPricingList.filter((pricing) => {
        const haystack = `${pricing?.productName || ""} ${pricing?.condition || ""}`.toLowerCase();
        return haystack.includes(search.toLowerCase());
      }),
    [dynamicPricingList, search]
  );

  const activeRules = dynamicPricingList.filter((pricing) => {
    if (!pricing.endDate) return true;
    return new Date(pricing.endDate) >= new Date();
  }).length;
  const randomDiscountCount = dynamicPricingList.filter((pricing) => pricing.isRandomDiscountEnabled).length;
  const averageDiscount =
    dynamicPricingList.length === 0
      ? 0
      : Math.round(
          dynamicPricingList.reduce(
            (sum, pricing) => sum + getDiscountPercent(pricing.basePrice, pricing.salePrice),
            0
          ) / dynamicPricingList.length
        );

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <div className="flex min-h-screen flex-col md:flex-row">
        <Sidebaradmin />

        <div className="flex min-w-0 flex-1 flex-col">
          <Navbaradmin />

          <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
            <section className="mb-6 flex flex-col justify-between gap-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:flex-row lg:items-center">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-sky-600">Pricing controls</p>
                <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
                  Dynamic Pricing
                </h1>
                <p className="mt-1 max-w-2xl text-sm text-slate-500">
                  Configure timed offers, sale prices, free months, and random discount rules across products.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:min-w-[420px]">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase text-slate-500">Rules</p>
                  <p className="mt-1 text-2xl font-bold text-slate-950">{dynamicPricingList.length}</p>
                </div>
                <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3">
                  <p className="text-xs font-semibold uppercase text-emerald-700">Active</p>
                  <p className="mt-1 text-2xl font-bold text-emerald-800">{activeRules}</p>
                </div>
                <div className="rounded-lg border border-sky-100 bg-sky-50 p-3">
                  <p className="text-xs font-semibold uppercase text-sky-700">Avg Off</p>
                  <p className="mt-1 text-2xl font-bold text-sky-800">{averageDiscount}%</p>
                </div>
              </div>
            </section>

            {(error || successMessage) && (
              <div
                className={`mb-6 rounded-lg border px-4 py-3 text-sm font-semibold ${
                  error
                    ? "border-rose-100 bg-rose-50 text-rose-700"
                    : "border-emerald-100 bg-emerald-50 text-emerald-700"
                }`}
              >
                {error || successMessage}
              </div>
            )}

            <section className="mb-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                <div>
                  <h2 className="text-lg font-bold text-slate-950">
                    {editingId ? "Edit Pricing Rule" : "Create Pricing Rule"}
                  </h2>
                  <p className="text-sm text-slate-500">
                    Choose a product and define the sale window, offer condition, and free-month bonus.
                  </p>
                </div>
                {editingId && (
                  <button
                    type="button"
                    className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    onClick={() => {
                      setEditingId(null);
                      setPricingData(emptyPricingData);
                      clearMessages();
                    }}
                  >
                    Cancel Edit
                  </button>
                )}
              </div>

              <form onSubmit={(event) => event.preventDefault()}>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                  <Field label="Product">
                    <select name="productId" value={pricingData.productId} onChange={handleChange} className={inputClass}>
                      <option value="">Select Product</option>
                      {products.map((product) => (
                        <option key={product._id} value={product._id}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Base Price">
                    <input
                      type="number"
                      name="basePrice"
                      value={pricingData.basePrice}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Base price"
                      disabled
                    />
                  </Field>

                  <Field label="Sale Price">
                    <input
                      type="number"
                      name="salePrice"
                      value={pricingData.salePrice}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Offer price"
                    />
                  </Field>

                  <Field label="Condition">
                    <input
                      type="text"
                      name="condition"
                      value={pricingData.condition}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="e.g. Flash sale, New users"
                    />
                  </Field>

                  <Field label="Start Date">
                    <input type="date" name="startDate" value={pricingData.startDate} onChange={handleChange} className={inputClass} />
                  </Field>

                  <Field label="End Date">
                    <input type="date" name="endDate" value={pricingData.endDate} onChange={handleChange} className={inputClass} />
                  </Field>

                  <Field label="Free Months">
                    <input
                      type="number"
                      name="freeMonths"
                      value={pricingData.freeMonths}
                      onChange={handleChange}
                      className={inputClass}
                      min="0"
                    />
                  </Field>

                  <div className="rounded-lg border border-sky-100 bg-sky-50 p-4 lg:col-span-2">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <div>
                        <p className="text-xs font-semibold uppercase text-sky-700">Discount</p>
                        <p className="mt-1 text-xl font-bold text-sky-900">
                          {getDiscountPercent(pricingData.basePrice, pricingData.salePrice)}%
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase text-sky-700">Customer Saves</p>
                        <p className="mt-1 text-xl font-bold text-sky-900">
                          {formatMoney(Number(pricingData.basePrice || 0) - Number(pricingData.salePrice || 0))}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase text-sky-700">Sale Price</p>
                        <p className="mt-1 text-xl font-bold text-sky-900">{formatMoney(pricingData.salePrice)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 flex justify-end">
                  <button
                    type="button"
                    onClick={editingId ? handleUpdatePricing : handleAddPricing}
                    className="inline-flex min-w-40 items-center justify-center rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-70"
                    disabled={loading}
                  >
                    {loading ? <Spinner animation="border" size="sm" /> : editingId ? "Update Rule" : "Create Rule"}
                  </button>
                </div>
              </form>
            </section>

            <section className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100">
                    <AiOutlineCheckCircle className="text-xl" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-950">{activeRules} active rules</p>
                    <p className="text-xs text-slate-500">Currently within pricing window</p>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50 text-sky-700 ring-1 ring-sky-100">
                    <AiOutlinePercentage className="text-xl" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-950">{randomDiscountCount} random discounts</p>
                    <p className="text-xs text-slate-500">Rules with dynamic discount enabled</p>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100">
                    <AiOutlineCalendar className="text-xl" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-950">{products.length} products available</p>
                    <p className="text-xs text-slate-500">Product catalog loaded for pricing</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-5 flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
                <div>
                  <h2 className="text-lg font-bold text-slate-950">Pricing Rules</h2>
                  <p className="text-sm text-slate-500">Search, edit, delete, and configure random discount settings.</p>
                </div>
                <label className="relative w-full sm:w-80">
                  <AiOutlineSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search pricing rules"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100"
                  />
                </label>
              </div>

              <ExistingDynamicPricingRules
                dynamicPricingList={filteredPricingList}
                handleDeletePricing={handleDeletePricing}
                handleStartEditPricing={handleStartEditPricing}
              />
            </section>
          </main>
        </div>
      </div>
    </div>
  );
};

export default DynamicPricingAdminPage;
